import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { FaInfoCircle } from 'react-icons/fa';

const AFFILIATE_PERCENTAGE = 25; // Affiliate percentage
const PROGRAM_DURATION = 24; // Program duration in months
const VAT_PERCENTAGE = 21; // VAT percentage

const FEATURES = [
  { name: 'Basic email communication', membership: 'Base' },
  { name: 'Template builder and AI-assisted emails', membership: 'Pro' },
  { name: 'Event attendance insights and export', membership: 'Base' },
  { name: 'Basic financial administration', membership: 'Base' },
  { name: 'Bank connection, saving pots, budgets', membership: 'Pro' },
];

const SUBSCRIPTION_TYPES = [
  { name: 'Free', price: 0, variableAmount: 0 },
  { name: 'Base', price: 7.5, variableAmount: 0.05 },
  { name: 'Pro', price: 12.5, variableAmount: 0.1 },
];

export default function AffiliateCalculator(): JSX.Element {
  const [proCommunities, setProCommunities] = useState(20);
  const [averageUsers, setAverageUsers] = useState(25);
  const [selectedFeatures, setSelectedFeatures] = useState(FEATURES.map(feature => feature.name));
  const { t } = useTranslation();

  const calculateSubscriptionPrice = () => {
    const subscriptionType = selectedFeatures.length === 0
      ? SUBSCRIPTION_TYPES.find(type => type.name === 'Free')
      : selectedFeatures.some(feature => {
          const featureData = FEATURES.find(f => f.name === feature);
          return featureData?.membership === 'Pro';
        })
      ? SUBSCRIPTION_TYPES.find(type => type.name === 'Pro')
      : SUBSCRIPTION_TYPES.find(type => type.name === 'Base');

    const basePrice = subscriptionType?.price || 0;
    const variableAmount = subscriptionType?.variableAmount || 0;

    return basePrice + variableAmount * averageUsers;
  };

  const calculateEarnings = () => {
    const subscriptionPrice = calculateSubscriptionPrice();
    return proCommunities * subscriptionPrice * (AFFILIATE_PERCENTAGE / 100) * (1 - VAT_PERCENTAGE / 100);
  };

  const calculateTotalEarnings = () => {
    const monthlyEarnings = calculateEarnings();
    return monthlyEarnings * PROGRAM_DURATION;
  };

  const handleFeatureChange = (feature: string) => {
    setSelectedFeatures(prev =>
      prev.includes(feature)
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  return (
    <div className="max-w-2xl mx-auto bg-secondary-200 bg-opacity-50 rounded-xl px-6 py-4 shadow-xl border border-secondary-200 border-opacity-30">
      <h2 className="text-center text-2xl font-semibold text-primary-900 mb-4">{t('page.affiliate.calculator.title')}</h2>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label className="font-medium text-primary-900">{t('page.affiliate.calculator.amountCommunities')}: {proCommunities}</label>
          <input
            type="range"
            min="1"
            max="100"
            value={proCommunities}
            onChange={e => setProCommunities(Number(e.target.value))}
            className="w-full h-2 bg-secondary-50 rounded-lg appearance-none cursor-pointer slider:bg-primary-300"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-medium text-primary-900">{t('page.affiliate.calculator.amountUsers')}: {averageUsers}</label>
          <input
            type="range"
            min="1"
            max="100"
            value={averageUsers}
            onChange={e => setAverageUsers(Number(e.target.value))}
            className="w-full h-2 bg-secondary-50 rounded-lg appearance-none cursor-pointer slider:bg-primary-300"
          />
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="font-medium text-primary-900">{t('page.affiliate.calculator.features')}</h3>
          <p className="font-small text-primary-900 opacity-75">Events, Polls, Scores, Tasks and Chats are always included.</p>
          <div className="flex flex-col gap-1">
            {FEATURES.map(feature => (
              <div key={feature.name} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedFeatures.includes(feature.name)}
                  onChange={() => handleFeatureChange(feature.name)}
                  className="h-5 w-5 cursor-pointer rounded-full border-primary bg-secondary-50 text-primary-300 accent-primary-900 focus:ring-0 focus:ring-transparent"
                />
                <span className="text-primary-900 font-medium bg-secondary-100 opacity-75 py-1 rounded">{feature.name}</span>
                <span className={twMerge("text-sm text-white p-1 rounded", feature.membership === 'Base' ? "bg-primary-300" : "bg-primary-900")}>{feature.membership}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-2 p-2 bg-secondary-50 bg-opacity-50 rounded-xl">
          <div className="flex items-center justify-center gap-2 mb-2">
            <h3 className="text-xl font-semibold text-primary-900">{t('page.affiliate.calculator.estimatedEarnings')}:</h3>
            <div className="relative group">
              <FaInfoCircle className="w-5 h-5 text-primary-900 cursor-help" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-pre-line z-10 w-80">
                <CalculationBreakdown 
                  proCommunities={proCommunities}
                  averageUsers={averageUsers}
                  selectedFeatures={selectedFeatures}
                />
              </div>
            </div>
          </div>
          <p className="text-2xl font-bold text-primary-900">€{calculateTotalEarnings().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="text-sm text-primary-900 opacity-75 mt-1">{t('page.affiliate.calculator.for')} {PROGRAM_DURATION} {t('page.affiliate.calculator.month')}</p>
        </div>
      </div>
    </div>
  );
}

interface CalculationBreakdownProps {
  proCommunities: number;
  averageUsers: number;
  selectedFeatures: string[];
}

function CalculationBreakdown({ 
  proCommunities, 
  averageUsers, 
  selectedFeatures
}: CalculationBreakdownProps): JSX.Element {
  const { t } = useTranslation();

  const subscriptionType = selectedFeatures.length === 0
    ? SUBSCRIPTION_TYPES.find(type => type.name === 'Free')
    : selectedFeatures.some(feature => {
        const featureData = FEATURES.find(f => f.name === feature);
        return featureData?.membership === 'Pro';
      })
    ? SUBSCRIPTION_TYPES.find(type => type.name === 'Pro')
    : SUBSCRIPTION_TYPES.find(type => type.name === 'Base');

  const basePrice = subscriptionType?.price || 0;
  const variableAmount = subscriptionType?.variableAmount || 0;

  const subscriptionEarnings = (basePrice + variableAmount * averageUsers) * (AFFILIATE_PERCENTAGE / 100) * proCommunities;
  const totalEarnings = (subscriptionEarnings) * PROGRAM_DURATION  * (1 - VAT_PERCENTAGE / 100);

  return (
    <div>
      <div>{t('page.affiliate.calculator.calculationBreakdown')}</div>
      <div>{t('page.affiliate.calculator.RequiredSubscription', { subscriptionType: subscriptionType?.name })}</div>
      <div>{t('page.affiliate.calculator.SubscriptionEarnings', { amount: subscriptionEarnings.toFixed(2) })}</div>
      <div>{t('page.affiliate.calculator.MonthlyEarnings', { amount: (subscriptionEarnings).toFixed(2) })}</div>
      <div>{t('page.affiliate.calculator.TotalEarnings', { amount: totalEarnings.toFixed(2) })}</div>
    </div>
  );
}